import React from 'react';
import { createPortal } from 'react-dom';
// import { Badge } from '@/components/ui/badge';
import { Badge } from './ui/badge';
import { translate } from '../lib/utils/translate';
import type { LabelKey } from '@/lib/data/labels';
import { ScoreBreakdownList } from "./ScoreBreakdownList";
import { formatScore, type ScoreTriple, calcBestScore, calcHighestAttainableScore } from '../calculator/calculator';
import { calcRecomRate } from '../calculator/recommendationRate';
import type { JupasProgram } from '../types';
import type { MERResult } from '../types';

// The score badge (e.g. "41.0/55.3 : 87% Strong") is hidden across the app: the
// percentage/strength wording reads as an admission prediction and is misleading.
// The underlying recommendationRate is still computed and used to rank results
// (see ResultsTable sortedResults). Flip this to true to bring the badge back.
const SHOW_SCORE_BADGE = false;

interface ScoreBadgeProps {
  jupas_id?: string;
  userScores?: Record<string, ScoreTriple>;
  activeSubjects?: string[];
  programs: JupasProgram[];
  ger?: MERResult;
  per?: MERResult;
}

function getScoreBadge(recommendationRate: number): { color: string; text: string; labelKey: LabelKey } {

  let percentage = `${Math.round(recommendationRate)}%`;
  let key: LabelKey;
  let color = '';

  if (recommendationRate < 0) { // -1, New Programme
    key = 'passingPhrase_newProgramme';
    color = 'default';
    percentage = '';
  } else if (recommendationRate >= 95) {
    key = 'passingPhrase_extremelySafe'; // "Extremely Safe"
    color = 'green';
  } else if (recommendationRate >= 85) {
    key = 'passingPhrase_veryStrong'; // "Very Strong"
    color = 'green';
  } else if (recommendationRate >= 75) {
    key = 'passingPhrase_solidPass'; // "Solid Chance"
    color = 'lightGreen';
  } else if (recommendationRate >= 65) {
    key = 'passingPhrase_moderatelySafe'; // "Likely"
    color = 'lightGreen'; // Changed from Yellow to LightGreen to encourage >65%
  } else if (recommendationRate >= 50) {
    // CRITICAL FIX: The 50-64% range is now "Marginal" (Yellow), not Red.
    // This matches the visual of being near the Median.
    key = 'passingPhrase_atRisk'; // Using existing key for marginal/borderline cases
    color = 'yellow';
  } else if (recommendationRate >= 40) {
    // 40-49% is "Risky" but not "Impossible". Use Orange.
    key = 'passingPhrase_atRisk'; // "High Risk"
    color = 'orange';
  } else {
    // < 40% is the only true "Danger Zone".
    key = 'passingPhrase_wellBelow'; // "Unlikely"
    color = 'red';
  }

  return { color, text: percentage, labelKey: key };
}


export const ScoreBadge: React.FC<ScoreBadgeProps> = (props) => {
  if (!SHOW_SCORE_BADGE) {
    return null;
  }

  return <ScoreBadgeInner {...props} />;
};

const ScoreBadgeInner: React.FC<ScoreBadgeProps> = ({
  jupas_id,
  userScores,
  activeSubjects,
  programs,
  ger,
  per,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMounted, setIsMounted] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const [shouldRenderBreakdown, setShouldRenderBreakdown] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const touchedRef = React.useRef(false);
  const suppressClickRef = React.useRef(false);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const effectiveUserScores = React.useMemo(() => {
    if (!userScores) {
      return {};
    }

    if (!activeSubjects?.length) {
      return userScores;
    }

    const activeSet = new Set(activeSubjects);
    return Object.fromEntries(
      Object.entries(userScores).filter(([subject]) => activeSet.has(subject))
    );
  }, [activeSubjects, userScores]);

  const safeJupasId = jupas_id ?? '';
  const isDataReady = Boolean(jupas_id && userScores && programs);

  const {
    cleanJupasId,
    currentScore,
    highestAttainableScore,
    recommendationRate,
  } = React.useMemo(() => {
    const nextCleanJupasId = safeJupasId.replace(/^JS/, '');
    const program = programs.find(p => p.jupas_id === nextCleanJupasId);
    const nextCurrentScore = calcBestScore(safeJupasId, effectiveUserScores, programs, 'current');
    const nextMinScore = calcBestScore(jupas_id, effectiveUserScores, programs, 'min');
    const nextMaxScore = calcBestScore(jupas_id, effectiveUserScores, programs, 'max');

    const instKey = program ? (() => {
      const firstChar = nextCleanJupasId.charAt(0);
      if (firstChar === '1') return '1';
      if (firstChar === '2') return '2';
      if (firstChar === '3') return '3';
      if (firstChar === '4') return '4';
      if (firstChar === '5') return '5';
      if (firstChar === '6') return '6';
      if (firstChar === '7') return '7';
      if (firstChar === '8') return '8';
      if (firstChar === '9') return '9';
      if (firstChar === 'S') return 'S';
      return undefined;
    })() : undefined;

    const nextHighestAttainableScore = program
      ? calcHighestAttainableScore(program, (instKey || '') as string)
      : 0;

    let nextRecommendationRate: number | undefined;
    if (program?.past_admission_scores && program.past_admission_scores.length > 0) {
      const latestScores = program.past_admission_scores[0];
      nextRecommendationRate = calcRecomRate(
        {
          min: nextMinScore,
          current: nextCurrentScore,
          max: nextMaxScore
        },
        {
          LQ: latestScores.LQ,
          MED: latestScores.MED,
          UQ: latestScores.UQ
        },
        nextCleanJupasId,
        nextHighestAttainableScore
      );
    }

    return {
      cleanJupasId: nextCleanJupasId,
      currentScore: nextCurrentScore,
      highestAttainableScore: nextHighestAttainableScore,
      recommendationRate: nextRecommendationRate,
    };
  }, [effectiveUserScores, jupas_id, programs, safeJupasId]);

  const meetsMerRequirements = (ger?.eligible ?? true) && (per?.eligible ?? true);
  const hasBreakdown = Boolean(jupas_id && Object.keys(effectiveUserScores).length > 0);

  const toggleOpen = React.useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const updatePanelPosition = React.useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') {
      return;
    }

    const rect = wrapperRef.current.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight ?? 0;
    const viewportPadding = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 8;
    const preferredWidth = Math.min(480, viewportWidth - viewportPadding * 2);
    const minLeft = viewportPadding + preferredWidth / 2;
    const maxLeft = viewportWidth - viewportPadding - preferredWidth / 2;
    const centeredLeft = rect.left + rect.width / 2;
    const availableBelow = viewportHeight - rect.bottom - viewportPadding - gap;
    const availableAbove = rect.top - viewportPadding - gap;
    const shouldOpenAbove = panelHeight > availableBelow && availableAbove > availableBelow;
    const unclampedTop = shouldOpenAbove
      ? rect.top - panelHeight - gap
      : rect.bottom + gap;
    const top = Math.max(
      viewportPadding,
      Math.min(unclampedTop, viewportHeight - viewportPadding - panelHeight)
    );

    setPanelStyle({
      position: 'fixed',
      top,
      left: Math.max(minLeft, Math.min(centeredLeft, maxLeft)),
      transform: 'translateX(-50%)',
      width: preferredWidth,
      maxWidth: `calc(100vw - ${viewportPadding * 2}px)`,
      maxHeight: `calc(100vh - ${viewportPadding * 2}px)`,
      overflowY: 'auto',
      zIndex: 1000,
    });
  }, []);

  // Close panel when tapping/clicking outside
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrapperRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [isOpen]);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePanelPosition();

    const handleViewportChange = () => updatePanelPosition();
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, updatePanelPosition]);

  React.useEffect(() => {
    if (!isOpen || !shouldRenderBreakdown || typeof window === 'undefined') {
      return;
    }

    const firstFrame = window.requestAnimationFrame(() => {
      updatePanelPosition();
      const secondFrame = window.requestAnimationFrame(() => {
        updatePanelPosition();
      });

      return () => window.cancelAnimationFrame(secondFrame);
    });

    return () => window.cancelAnimationFrame(firstFrame);
  }, [isOpen, shouldRenderBreakdown, updatePanelPosition]);

  // Cleanup hover timer on unmount
  React.useEffect(() => {
    return () => {
      clearTimeout(hoverTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setShouldRenderBreakdown(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderBreakdown(true);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  // Build the badge text with optional score display
  const badgeText = [];
  let color = 'default'

  if (!meetsMerRequirements) {
    badgeText.push('ER not passed');
    color = 'red';
  } else if (recommendationRate !== undefined) {
    const badge = getScoreBadge(recommendationRate);
    // Add scores if both are provided
    if (currentScore !== undefined && highestAttainableScore !== undefined) {
      badgeText.push(`${formatScore(currentScore)}/${formatScore(highestAttainableScore)}`);
      badgeText.push(': ')
    }
    badgeText.push(badge.text);
    // Add translated label
    badgeText.push(translate(badge.labelKey));
    color = badge.color;
  } else {
    if (currentScore !== undefined && highestAttainableScore !== undefined) {
      badgeText.push(translate('score') + ':');
      badgeText.push(`${formatScore(currentScore)}/${formatScore(highestAttainableScore)}`);
    }
  }

  const badgeInner = (
    <Badge
      variant={color as 'default' | 'green' | 'lightGreen' | 'yellow' | 'orange' | 'red'}
      className={hasBreakdown ? "cursor-pointer" : ""}
    >
      {badgeText.join(' ')}
    </Badge>
  );

  if (!isDataReady) {
    return <Badge variant="default">-</Badge>;
  }

  if (!hasBreakdown) {
    return badgeInner;
  }

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={() => {
        if (touchedRef.current) return;
        clearTimeout(closeTimerRef.current);
        hoverTimerRef.current = setTimeout(() => setIsOpen(true), 300);
      }}
      onMouseLeave={() => {
        if (touchedRef.current) return;
        clearTimeout(hoverTimerRef.current);
        closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
      }}
    >
      <button
        type="button"
        className="inline-flex border-0 bg-transparent p-0"
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
        onTouchEnd={(e) => {
          e.preventDefault();
          touchedRef.current = true;
          suppressClickRef.current = true;
          toggleOpen();
          setTimeout(() => { touchedRef.current = false; }, 400);
          setTimeout(() => { suppressClickRef.current = false; }, 400);
        }}
        onClick={(e) => {
          if (suppressClickRef.current) {
            e.preventDefault();
            return;
          }

          toggleOpen();
        }}
      >
        {badgeInner}
      </button>
      {isOpen && isMounted && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="animate-in fade-in zoom-in duration-200 rounded-lg border border-slate-300 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.18)]"
          onMouseEnter={() => clearTimeout(closeTimerRef.current)}
          onMouseLeave={() => {
            closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
          }}
        >
          <div className="max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg">
            {shouldRenderBreakdown ? (
              <ScoreBreakdownList
                jupas_id={cleanJupasId}
                userScores={effectiveUserScores}
                activeSubjects={activeSubjects}
                selectedScoreType="current"
                translate={translate}
                showCollapseToggle={false}
              />
            ) : (
              <div className="rounded-lg bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800">
                {translate('scoreBreakdown')}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
